/**
 * Recent Activity Feed
 *
 * Timeline view showing recent activity from project and task audit logs.
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Activity,
  FolderKanban,
  CheckSquare,
  FileText,
  Plus,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Archive,
  RotateCcw,
} from 'lucide-react';
import type { ActivityItem } from '../types';

interface RecentActivityFeedProps {
  data?: ActivityItem[];
  loading?: boolean;
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/Montreal' });
}

function getActionIcon(action: string) {
  switch (action) {
    case 'project_created':
    case 'created':
      return <Plus className="h-3 w-3" />;
    case 'project_updated':
    case 'updated':
      return <Edit className="h-3 w-3" />;
    case 'project_archived':
    case 'archived':
      return <Archive className="h-3 w-3" />;
    case 'project_unarchived':
      return <RotateCcw className="h-3 w-3" />;
    case 'milestone_completed':
    case 'operations_stage_advanced':
    case 'operations_milestone_completed':
    case 'status_changed':
      return <CheckCircle className="h-3 w-3" />;
    case 'milestone_uncompleted':
    case 'operations_milestone_uncompleted':
      return <XCircle className="h-3 w-3" />;
    case 'deleted':
      return <Trash2 className="h-3 w-3" />;
    default:
      return <Activity className="h-3 w-3" />;
  }
}

function getActionLabel(action: string): string {
  const labels: Record<string, string> = {
    project_created: 'created project',
    project_updated: 'updated project',
    project_archived: 'archived project',
    project_unarchived: 'restored project',
    status_changed: 'changed status',
    milestone_completed: 'completed milestone',
    milestone_uncompleted: 'reverted milestone',
    operations_stage_advanced: 'advanced quote',
    operations_milestone_completed: 'completed quote milestone',
    operations_milestone_uncompleted: 'reverted quote milestone',
    operations_notes_updated: 'updated quote notes',
    created: 'created task',
    updated: 'updated task',
    archived: 'archived task',
    comment_added: 'added comment',
    comment_deleted: 'deleted comment',
    attachment_added: 'added attachment',
    attachment_deleted: 'deleted attachment',
  };
  return labels[action] || action.replace(/_/g, ' ');
}

function getEntityColor(entityType: 'project' | 'task' | 'quote'): string {
  if (entityType === 'project') return 'bg-blue-500';
  if (entityType === 'quote') return 'bg-amber-500';
  return 'bg-green-500';
}

function getEntityIcon(entityType: 'project' | 'task' | 'quote') {
  if (entityType === 'project') return <FolderKanban className="h-3 w-3" />;
  if (entityType === 'quote') return <FileText className="h-3 w-3" />;
  return <CheckSquare className="h-3 w-3" />;
}

export function RecentActivityFeed({ data, loading }: RecentActivityFeedProps) {
  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <Skeleton className="h-5 w-32" />
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex gap-3">
                <Skeleton className="h-6 w-6 rounded-full" />
                <div className="flex-1 space-y-1">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/4" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const hasData = data && data.length > 0;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Activity className="h-4 w-4" />
          Recent Activity
        </CardTitle>
      </CardHeader>
      <CardContent>
        {hasData ? (
          <ScrollArea className="h-[280px]">
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-3 top-0 bottom-0 w-px bg-border" />

              <div className="space-y-4 pr-4">
                {data.map((item) => (
                  <div key={item.id} className="relative flex gap-4 pl-1">
                    {/* Timeline dot */}
                    <div
                      className={`relative z-10 flex h-6 w-6 items-center justify-center rounded-full text-white ${getEntityColor(
                        item.entity_type
                      )}`}
                    >
                      {getEntityIcon(item.entity_type)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0 pb-4">
                      <div className="flex items-start gap-2">
                        <span className="text-sm text-muted-foreground">
                          {getActionIcon(item.action)}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm">
                            <span className="font-medium">{item.actor_name}</span>
                            {' '}
                            <span className="text-muted-foreground">
                              {getActionLabel(item.action)}
                            </span>
                            {' '}
                            <span className="font-medium truncate">{item.entity_title}</span>
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {formatRelativeTime(item.created_at)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </ScrollArea>
        ) : (
          <div className="flex items-center justify-center h-[280px] text-muted-foreground text-sm">
            No recent activity
          </div>
        )}
      </CardContent>
    </Card>
  );
}
